import * as z from "zod";

export const MaterialStatusEnum = z.enum(["active", "inactive", "draft"]);

export const MaterialTypeEnum = z.enum([
  "cardboard",
  "plastic",
  "metal",
  "fabric",
  "vinyl",
  "reflective",
  "magnetic",
  "foam",
  "composite",
  "other",
]);

export const MaterialFinishEnum = z.enum([
  "matte",
  "glossy",
  "satin",
  "textured",
  "smooth",
  "brushed",
  "embossed",
  "laminated",
  "other",
]);

export const MaterialSchema = z.object({
  id: z.string().uuid(),
  name: z
    .string()
    .min(1, "Name is required")
    .max(255, "Name must be less than 255 characters")
    .trim(),
  description: z
    .string()
    .max(255, "Description must be less than 255 characters")
    .nullable()
    .optional(),
  type: MaterialTypeEnum.nullable().optional(),
  color: z
    .string()
    .max(7, "Color must be a valid hex code")
    .regex(/^#[0-9A-Fa-f]{6}$/, "Color must be a valid hex code")
    .nullable()
    .optional(),
  finish: MaterialFinishEnum.nullable().optional(),
  ref: z
    .string()
    .max(10, "Reference code must be less than 10 characters")
    .nullable()
    .optional(),
  material_code: z
    .string()
    .max(50, "Material code must be less than 50 characters")
    .nullable()
    .optional(),
  brand: z
    .string()
    .max(100, "Brand must be less than 100 characters")
    .nullable()
    .optional(),
  class: z
    .string()
    .max(10, "Class must be less than 10 characters")
    .nullable()
    .optional(),
  // Purchase dimensions - in mm (stored as strings to maintain precision)
  purchase_width: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Width must be positive (in mm)"
    )
    .nullable()
    .optional(),
  purchase_height: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Height must be positive (in mm)"
    )
    .nullable()
    .optional(),
  purchase_length: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Length must be positive (in mm)"
    )
    .nullable()
    .optional(),
  purchase_weight: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toString();
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Weight must be positive"
    )
    .nullable()
    .optional(),
  purchase_price: z
    .string()
    .transform((val) => {
      if (val === "" || val === null || val === undefined) return null;
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error("Invalid number");
      return num.toFixed(2); // Format to 2 decimal places for currency
    })
    .refine(
      (val) => val === null || parseFloat(val) >= 0,
      "Price must be positive"
    )
    .nullable()
    .optional(),
  recyclable: z.boolean().default(false),
  images: z.array(z.string().url("Must be a valid URL")).nullable().optional(),
  status: MaterialStatusEnum.default("draft"),
  created: z
    .date()
    .optional()
    .default(() => new Date()),
  updated: z
    .date()
    .optional()
    .default(() => new Date()),
});

export const createMaterialSchema = MaterialSchema.omit({
  id: true,
  created: true,
  updated: true,
});

export const updateMaterialSchema = MaterialSchema.partial().omit({
  id: true,
  created: true,
  updated: true,
});
